import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Alert,
  Spin,
  Tabs,
  Card,
  Row,
  Col,
  Tag,
  Statistic,
  Typography,
  Space,
  Divider,
  Timeline,
  Empty,
  Tooltip,
  Radio,
  Input,
  Form,
  message,
} from "antd";
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  InboxOutlined,
  StopOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  DollarOutlined,
  BankOutlined,
  UserOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

/**
 * Modal de confirmation avancée pour la suppression sécurisée des utilisateurs
 * Affiche les dépendances, alternatives et permet de choisir l'action appropriée
 */
const DeleteConfirmationModal = ({
  visible,
  onCancel,
  onConfirm,
  entity = null,
  entityType = "utilisateur",
  entityName = null,
  apiEndpoint = "/api/users",
  loading = false,
  // Backward compatibility
  user = null,
  userType = null,
}) => {
  // Support legacy props
  const actualEntity = entity || user;
  const actualEntityType = entityType || userType || "utilisateur";
  const actualEntityName = entityName || actualEntity?.name || actualEntity?.email;
  const [validationData, setValidationData] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [selectedAction, setSelectedAction] = useState("check"); // check, delete, archive, disable
  const [reason, setReason] = useState("");
  const [processingAction, setProcessingAction] = useState(false);
  const [form] = Form.useForm();

  // *** ACCÈS AUX INFORMATIONS DE L'UTILISATEUR CONNECTÉ ***
  const { user: currentUser, hasPermission } = useAuth();

  // Charger les dépendances quand le modal s'ouvre
  useEffect(() => {
    if (visible && actualEntity?.id) {
      loadEntityDependencies();
    }
  }, [visible, actualEntity?.id]);

  /**
   * Charge les dépendances de l'entité depuis l'API
   */
  const loadEntityDependencies = async () => {
    setValidationLoading(true);
    try {
      // Configuration timeout pour éviter ETIMEDOUT avec retry automatique
      const response = await axios.get(`${apiEndpoint}/${actualEntity.id}/dependencies`, {
        timeout: 30000, // 30 secondes max
        retry: 2,       // 2 tentatives si échec
        retryDelay: 1000 // 1 seconde entre tentatives
      });
      setValidationData(response.data);

      // Définir l'action par défaut basée sur la possibilité de suppression
      if (response.data.canDelete) {
        setSelectedAction("delete");
      } else {
        // Trouver l'action recommandée
        const recommended = response.data.alternatives?.find(
          (alt) => alt.recommended
        );
        setSelectedAction(recommended?.action || "archive");
      }
    } catch (error) {
      console.error("Erreur lors du chargement des dépendances:", error);

      // Gestion spécifique des erreurs de timeout
      if (error.code === 'ETIMEDOUT' || error.code === 'ECONNABORTED') {
        message.error(`Timeout lors de la vérification des dépendances de ${actualEntityType}. Veuillez réessayer.`);
      } else if (error.response?.status === 500) {
        message.error(`Erreur serveur lors de la vérification des dépendances de ${actualEntityType}.`);
      } else {
        message.error(`Impossible de vérifier les dépendances de ${actualEntityType}`);
      }

      // *** FIX: Définir des données par défaut pour permettre l'utilisation du modal ***
      // En cas d'échec de validation, on assume des dépendances et force la suppression forcée
      setValidationData({
        canDelete: false, // *** IMPORTANT: false pour forcer la suppression forcée ***
        dependencies: [
          {
            type: "unknown",
            message: "Impossible de vérifier les dépendances (erreur API)",
            critical: true
          }
        ],
        summary: {
          totalDependencies: 1,
          criticalDependencies: 1,
          lastActivity: null
        },
        alternatives: [
          { action: "delete", recommended: true },
          { action: "archive", recommended: false },
          { action: "disable", recommended: false }
        ],
        // *** AJOUT: Metadata requis pour le bypass du service ***
        metadata: {
          productId: parseInt(actualEntity.id, 10), // *** IMPORTANT: Conversion en number pour correspondre au backend ***
          checkDate: new Date().toISOString(),
          fallbackReason: "API dependencies call failed"
        }
      });
      setSelectedAction("delete");
    } finally {
      setValidationLoading(false);
    }
  };

  /**
   * Exécute l'action sélectionnée
   */
  const handleActionConfirm = async () => {
    if (!validationData || !selectedAction || selectedAction === "check") {
      return;
    }

    // Validation du formulaire si une raison est requise
    if (
      (selectedAction === "delete" && !validationData.canDelete) ||
      selectedAction === "archive" ||
      selectedAction === "disable"
    ) {
      try {
        await form.validateFields();
      } catch {
        return;
      }
    }

    setProcessingAction(true);

    try {
      let response;
      const actionReason =
        reason || `${getActionLabel(selectedAction)} via interface utilisateur`;

      switch (selectedAction) {
        case "delete":
          if (validationData.canDelete) {
            // Le serveur refait une validation fraîche avant la suppression normale.
            response = await axios.delete(`${apiEndpoint}/${actualEntity.id}`, {
              data: {
                reason: actionReason
              },
            });
          } else {
            // Suppression forcée (nécessite permissions admin)
            response = await axios.delete(`${apiEndpoint}/${actualEntity.id}/force`, {
              data: {
                reason: actionReason,
                force: true
              },
            });
          }
          break;

        case "archive":
          response = await axios.patch(`${apiEndpoint}/${actualEntity.id}/archive`, {
            reason: actionReason,
            skipValidation: true, // *** BYPASS DE LA RE-VALIDATION ***
            preValidatedData: validationData // Transmettre les données déjà validées
          });
          break;

        case "disable":
          response = await axios.patch(`${apiEndpoint}/${actualEntity.id}/disable`, {
            reason: actionReason,
            skipValidation: true, // *** BYPASS DE LA RE-VALIDATION ***
            preValidatedData: validationData // Transmettre les données déjà validées
          });
          break;

        default:
          throw new Error("Action non supportée");
      }

      if (response.data.success) {
        message.success(getSuccessMessage(selectedAction));
        onConfirm(selectedAction, response.data);
      } else {
        throw new Error(response.data.error || "Action échouée");
      }
    } catch (error) {
      console.error("Erreur lors de l'exécution de l'action:", error);

      let errorMessage = "Erreur lors de l'exécution de l'action";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    } finally {
      setProcessingAction(false);
    }
  };

  /**
   * Obtient le libellé de l'action
   */
  const getActionLabel = (action) => {
    const labels = {
      delete: "Suppression",
      archive: "Archivage",
      disable: "Désactivation",
    };
    return labels[action] || action;
  };

  /**
   * Obtient le message de succès pour l'action
   */
  const getSuccessMessage = (action) => {
    const messages = {
      delete: `${actualEntityType} supprimé avec succès`,
      archive: `${actualEntityType} archivé avec succès`,
      disable: `${actualEntityType} désactivé avec succès`,
    };
    return messages[action] || "Action exécutée avec succès";
  };

  /**
   * Obtient l'icône pour un type de dépendance
   */
  const getDependencyIcon = (type) => {
    const icons = {
      orders: <ShoppingOutlined />,
      expenses: <DollarOutlined />,
      financial: <BankOutlined />,
      assignments: <UserOutlined />,
      ecommerce: <ShoppingOutlined />,
    };
    return icons[type] || <InfoCircleOutlined />;
  };

  /**
   * Obtient la couleur pour un niveau de criticité
   */
  const getCriticalityColor = (critical) => {
    return critical ? "error" : "warning";
  };

  /**
   * Rendu de la section des dépendances
   */
  const renderDependencies = () => {
    if (
      !validationData?.dependencies ||
      validationData.dependencies.length === 0
    ) {
      return (
        <Empty
          image={
            <CheckCircleOutlined style={{ fontSize: 48, color: "#52c41a" }} />
          }
          description="Aucune dépendance détectée"
        >
          <Text type="success">
            Cette entité peut être supprimée en toute sécurité.
          </Text>
        </Empty>
      );
    }

    return (
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {validationData.dependencies.map((dep, index) => (
          <Card
            key={index}
            size="small"
            style={{
              borderLeft: `4px solid ${dep.critical ? "#ff4d4f" : "#faad14"}`,
              backgroundColor: dep.critical ? "#fff2f0" : "#fffbf0",
            }}
          >
            <Row align="middle" gutter={16}>
              <Col flex="none">
                <div
                  style={{
                    fontSize: 24,
                    color: dep.critical ? "#ff4d4f" : "#faad14",
                  }}
                >
                  {getDependencyIcon(dep.type)}
                </div>
              </Col>
              <Col flex="auto">
                <Space direction="vertical" size={2}>
                  <Text strong>{dep.message}</Text>
                  {dep.details && Object.keys(dep.details).length > 0 && (
                    <Space size="middle">
                      {dep.details.unpaidCount > 0 && (
                        <Tag color="red">
                          {dep.details.unpaidCount} non payée(s)
                        </Tag>
                      )}
                      {dep.details.totalAmount && (
                        <Tag color="blue">
                          Total: {dep.details.totalAmount.toLocaleString()} FCFA
                        </Tag>
                      )}
                      {dep.details.lastOrderDate && (
                        <Tag color="orange">
                          Dernier:{" "}
                          {new Date(
                            dep.details.lastOrderDate
                          ).toLocaleDateString()}
                        </Tag>
                      )}
                    </Space>
                  )}
                </Space>
              </Col>
              <Col flex="none">
                <Tag color={getCriticalityColor(dep.critical)}>
                  {dep.critical ? "Critique" : "Attention"}
                </Tag>
              </Col>
            </Row>
          </Card>
        ))}
      </Space>
    );
  };

  /**
   * Rendu des statistiques
   */
  const renderStatistics = () => {
    if (!validationData) return null;

    // *** PATTERN COHÉRENT USERS/PRODUITS : Utiliser 'summary' avec fallbacks ***
    const totalDependencies = validationData.summary?.totalDependencies ||
                             validationData.summaryStats?.totalDependencies ||
                             validationData.metadata?.totalDependencies ||
                             validationData.dependencies?.length || 0;
    const criticalDependencies = validationData.summary?.criticalDependencies ||
                                validationData.summaryStats?.criticalDependencies ||
                                validationData.metadata?.criticalDependencies ||
                                validationData.dependencies?.filter(dep => dep.critical).length || 0;
    const lastActivity = validationData.summary?.lastActivity ||
                        validationData.summaryStats?.lastActivity;
    const lastActivityTimestamp = lastActivity
      ? new Date(lastActivity).getTime()
      : NaN;
    const lastActivityDays = Number.isFinite(lastActivityTimestamp)
      ? Math.max(
          0,
          Math.floor(
            (Date.now() - lastActivityTimestamp) / (1000 * 60 * 60 * 24)
          )
        )
      : null;

    return (
      <Row gutter={16}>
        <Col span={8}>
          <Statistic
            title="Dépendances totales"
            value={totalDependencies}
            prefix={<InfoCircleOutlined />}
            valueStyle={{
              color: totalDependencies > 0 ? "#faad14" : "#52c41a",
            }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="Critiques"
            value={criticalDependencies}
            prefix={<ExclamationCircleOutlined />}
            valueStyle={{
              color: criticalDependencies > 0 ? "#ff4d4f" : "#52c41a",
            }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="Dernière activité"
            value={lastActivityDays ?? "N/A"}
            suffix={lastActivityDays !== null ? "jours" : ""}
            prefix={<ClockCircleOutlined />}
          />
        </Col>
      </Row>
    );
  };

  /**
   * Rendu des actions possibles
   */
  const renderActionSelection = () => {
    if (!validationData) return null;

    // *** VÉRIFICATION DES PERMISSIONS SPÉCIFIQUES SELON LE TYPE D'ENTITÉ ***
    let canDelete, canForceDelete, canArchive, canDisable;

    if (actualEntityType === "produit" || apiEndpoint.includes("/produits")) {
      canDelete = hasPermission("Gestion Commerciale.Produits.Produits.delete");
      canForceDelete = hasPermission("Gestion Commerciale.Produits.Produits.force_delete");
      canArchive = hasPermission("Gestion Commerciale.Produits.Produits.archive");
      canDisable = hasPermission("Gestion Commerciale.Produits.Produits.disable");
    } else {
      // Pour les utilisateurs ou autres entités, utiliser la logique existante
      canDelete = true; // Permissions gérées par le backend
      canForceDelete = currentUser?.is_superadmin || false;
      canArchive = true;
      canDisable = true;
    }

    const actions = [
      {
        key: "delete",
        label: "Supprimer définitivement",
        icon: <DeleteOutlined />,
        color: "#ff4d4f",
        disabled: validationData.canDelete ? !canDelete : !canForceDelete,
        description: validationData.canDelete
          ? "Suppression sécurisée sans dépendances"
          : "Suppression forcée (administrateurs uniquement)",
        requiresReason: !validationData.canDelete,
      },
      {
        key: "archive",
        label: `Archiver ${actualEntityType === 'produit' ? 'le produit' : 'l\'entité'}`,
        icon: <InboxOutlined />,
        color: "#173f5f",
        disabled: !canArchive,
        description: "Conserver les données avec possibilité de restauration",
        requiresReason: true,
      },
      {
        key: "disable",
        label: "Désactiver temporairement",
        icon: <StopOutlined />,
        color: "#faad14",
        disabled: !canDisable,
        description: "Bloquer l'accès sans supprimer les données",
        requiresReason: true,
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Radio.Group
          value={selectedAction}
          onChange={(e) => setSelectedAction(e.target.value)}
          style={{ width: "100%" }}
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {actions.map((action) => {
              const isRecommended = validationData.alternatives?.find(
                (alt) => alt.action === action.key && alt.recommended
              );

              return (
                <Card
                  key={action.key}
                  size="small"
                  style={{
                    borderColor:
                      selectedAction === action.key ? action.color : undefined,
                    opacity: action.disabled ? 0.6 : 1,
                  }}
                >
                  <Radio
                    value={action.key}
                    disabled={action.disabled}
                    style={{ width: "100%" }}
                  >
                    <Row align="middle" gutter={16}>
                      <Col flex="none">
                        <div style={{ fontSize: 18, color: action.color }}>
                          {action.icon}
                        </div>
                      </Col>
                      <Col flex="auto">
                        <Space direction="vertical" size={2}>
                          <Space>
                            <Text strong>{action.label}</Text>
                            {isRecommended && (
                              <Tag color="green" size="small">
                                Recommandé
                              </Tag>
                            )}
                            {action.disabled && (
                              <Tag color="red" size="small">
                                Non autorisé
                              </Tag>
                            )}
                          </Space>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {action.description}
                          </Text>
                        </Space>
                      </Col>
                    </Row>
                  </Radio>
                </Card>
              );
            })}
          </Space>
        </Radio.Group>

        {/* Champ raison si requis */}
        {selectedAction !== "check" &&
          ((selectedAction === "delete" && !validationData.canDelete) ||
            selectedAction === "archive" ||
            selectedAction === "disable") && (
            <Form form={form} layout="vertical">
              <Form.Item
                name="reason"
                label="Raison (obligatoire)"
                rules={[
                  { required: true, message: "Veuillez préciser la raison" },
                ]}
              >
                <Input.TextArea
                  placeholder={`Précisez la raison de ${getActionLabel(
                    selectedAction
                  ).toLowerCase()}...`}
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Form.Item>
            </Form>
          )}
      </Space>
    );
  };

  /**
   * Obtient la couleur du bouton de confirmation
   */
  const getConfirmButtonProps = () => {
    if (selectedAction === "delete") {
      return { danger: true, icon: <DeleteOutlined /> };
    } else if (selectedAction === "archive") {
      return { type: "primary", icon: <InboxOutlined /> };
    } else if (selectedAction === "disable") {
      return { type: "default", icon: <StopOutlined /> };
    }
    return { type: "primary" };
  };

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: "#faad14" }} />
          Gestion de {actualEntityType} - {actualEntityName}
        </Space>
      }
      visible={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="confirm"
          {...getConfirmButtonProps()}
          onClick={handleActionConfirm}
          loading={processingAction}
          disabled={
            !validationData || selectedAction === "check" || validationLoading
          }
        >
          {processingAction ? "Traitement..." : getActionLabel(selectedAction)}
        </Button>,
      ]}
      destroyOnClose
    >
      <Spin spinning={validationLoading} tip="Vérification des dépendances...">
        {validationData && (
          <Tabs defaultActiveKey="dependencies" size="small">
            <TabPane tab="Dépendances" key="dependencies">
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                {renderStatistics()}
                <Divider />
                {renderDependencies()}
              </Space>
            </TabPane>

            <TabPane tab="Actions" key="actions">
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <Alert
                  message="Choisissez l'action appropriée"
                  description="Analysez les dépendances avant de choisir comment procéder avec cet utilisateur."
                  type="info"
                  icon={<SafetyOutlined />}
                  showIcon
                />
                {renderActionSelection()}
              </Space>
            </TabPane>

            {validationData.user && (
              <TabPane tab="Informations" key="info">
                <Card size="small">
                  <Space
                    direction="vertical"
                    size="middle"
                    style={{ width: "100%" }}
                  >
                    <Row gutter={16}>
                      <Col span={12}>
                        <Text strong>Nom:</Text> {validationData.user.name}
                      </Col>
                      <Col span={12}>
                        <Text strong>Email:</Text> {validationData.user.email}
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Text strong>Type:</Text>{" "}
                        {validationData.user.user_type}
                      </Col>
                      <Col span={12}>
                        <Text strong>Statut:</Text>
                        <Tag
                          color={
                            validationData.user.status === "active"
                              ? "green"
                              : "orange"
                          }
                        >
                          {validationData.user.status}
                        </Tag>
                      </Col>
                    </Row>
                  </Space>
                </Card>
              </TabPane>
            )}
          </Tabs>
        )}
      </Spin>
    </Modal>
  );
};

export default DeleteConfirmationModal;
